import React, { memo, useCallback, useMemo, useState } from 'react';
import { Chip, emphasize, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ClassNameMap } from '@mui/styles';
import { useIntl } from 'react-intl';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { useChipState } from '../../hooks/useChipState';
import { useIsSuggestion } from '../../hooks/useIsSuggestion';
import { useFeatureFlag } from '../../hooks';
import { useLabelState } from '../../../timesheet-labels/hooks/useLabelState';
import { LabelState } from '../../../timesheet-labels/hooks';

type ReadonlyLabelChipProps = {
  className?: string;
  label?: TimesheetLabel | null;
  isChipClickable?: boolean;
  size?: 'medium' | 'small';
  chipRef?: React.MutableRefObject<HTMLDivElement | null>;
  classes?: ClassNameMap;
  isButton?: boolean;
};

interface UseChipStylesProps {
  classes: ClassNameMap<string> | undefined;
  isEmptyLabelsEnabled: boolean;
  labelState: LabelState;
}

const useChipStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '100%',
    minWidth: '30px'
  },
  clickableColorPrimary: ({
    isEmptyLabelsEnabled,
    labelState
  }: UseChipStylesProps) => ({
    ...(isEmptyLabelsEnabled
      ? {}
      : {
          color: theme.palette.timesheetLabel.chipClickableColor,
          backgroundColor: theme.palette.timesheetLabel.nonEmptylabel
        }),
    '&:hover, &:focus': {
      background: emphasize(
        !isEmptyLabelsEnabled
          ? theme.palette.timesheetLabel.nonEmptylabel
          : labelState === LabelState.OMITTED
          ? theme.palette.primary.light
          : theme.palette.primary.main,
        0.08
      )
    },
    '&:active': {
      background: emphasize(
        !isEmptyLabelsEnabled
          ? theme.palette.timesheetLabel.nonEmptylabel
          : labelState === LabelState.OMITTED
          ? theme.palette.primary.light
          : theme.palette.primary.main,
        0.12
      )
    }
  }),
  nonClickableColorPrimary: ({
    isEmptyLabelsEnabled,
    labelState
  }: UseChipStylesProps) => ({
    ...(isEmptyLabelsEnabled
      ? {}
      : {
          color: theme.palette.timesheetLabel.chipClickableColor,
          backgroundColor: theme.palette.timesheetLabel.nonEmptylabel
        }),
    '&:hover, &:focus': {
      background:
        labelState === LabelState.SUGGESTED
          ? undefined
          : emphasize(
              !isEmptyLabelsEnabled
                ? theme.palette.timesheetLabel.nonEmptylabel
                : labelState === LabelState.OMITTED
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              0.08
            )
    },
    '&:active': {
      background:
        labelState === LabelState.SUGGESTED
          ? undefined
          : emphasize(
              !isEmptyLabelsEnabled
                ? theme.palette.timesheetLabel.nonEmptylabel
                : labelState === LabelState.OMITTED
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              0.12
            )
    }
  }),
  //TIDP-1774: Remove suggestionOutlinedSecondary and outlinedSecondary when isEmptyLabelsEnabled is fully rolled out.
  suggestionOutlinedSecondary: {
    color: theme.palette.timesheetLabel.nonEmptylabel,
    borderColor: theme.palette.timesheetLabel.nonEmptylabel,
    borderStyle: 'dashed'
  },
  suggestionOutlinedPrimary: {
    borderStyle: 'dashed'
  },
  omissionOutlinedPrimary: {
    backgroundColor: theme.palette.primary.light
  },
  outlinedSecondary: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    borderStyle: 'solid'
  }
}));

const useTooltipStyles = makeStyles((theme) => ({
  popper: {
    zIndex: theme.zIndex.tooltip + 2,
    pointerEvents: 'none'
  }
}));

const ReadonlyLabelChipFN: React.FC<ReadonlyLabelChipProps> = ({
  className,
  label,
  isChipClickable = true,
  size = 'medium',
  chipRef,
  classes: overrideClasses,
  isButton
}: ReadonlyLabelChipProps) => {
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  const isSuggestion = useIsSuggestion(label);
  const labelState = useLabelState(label);
  const classes = useChipStyles({
    classes: overrideClasses,
    isEmptyLabelsEnabled: isEmptyLabelsEnabled,
    labelState: labelState
  });
  const tooltipClasses = useTooltipStyles();
  const { formatMessage } = useIntl();
  const { isEmptyValue: isEmpty, chipLabel, toolTipText } = useChipState(label);
  const chipClasses = useMemo(() => {
    const cls: ClassNameMap = {
      root: classes.root,
      colorPrimary: classes.nonClickableColorPrimary,
      clickableColorPrimary: classes.clickableColorPrimary
    };
    //TIDP-1774: Remove outlinedSecondary when isEmptyLabelsEnabled is fully rolled out.
    cls['outlinedSecondary'] = isEmptyLabelsEnabled
      ? ''
      : isSuggestion && !isEmpty
      ? classes.suggestionOutlinedSecondary
      : classes.outlinedSecondary;
    cls['outlinedPrimary'] =
      isEmptyLabelsEnabled && labelState === LabelState.SUGGESTED
        ? classes.suggestionOutlinedPrimary
        : labelState === LabelState.OMITTED
        ? classes.omissionOutlinedPrimary
        : '';
    return cls;
  }, [classes, isSuggestion, isEmpty, isEmptyLabelsEnabled, labelState]);

  const clickableChiptestID = useMemo(() => {
    let chipState;
    if (isEmptyLabelsEnabled) {
      if (labelState === LabelState.NOT_SELECTED) {
        chipState = 'empty';
      } else if (labelState === LabelState.OMITTED) {
        chipState = 'omitted';
      } else if (labelState === LabelState.SUGGESTED) {
        chipState = 'suggestion';
      } else {
        chipState = 'hasValue';
      }
    } else {
      chipState =
        isSuggestion && !isEmpty
          ? 'suggestion'
          : isEmpty
          ? 'empty'
          : 'hasValue';
    }

    return `chip_${chipState}_${label?.name}_${label?.id}`;
  }, [
    isEmpty,
    isEmptyLabelsEnabled,
    isSuggestion,
    label?.id,
    label?.name,
    labelState
  ]);

  const labelComponent = useMemo(() => {
    return <Typography variant="caption">{chipLabel}</Typography>;
  }, [chipLabel]);

  const [showTooltip, setShowTooltip] = useState(false);

  const showTooltipFn = useCallback(() => setShowTooltip(true), [
    setShowTooltip
  ]);
  const hideTooltipFn = useCallback(() => setShowTooltip(false), [
    setShowTooltip
  ]);

  const ariaLabelMessage = useMemo(() => {
    if (isEmpty) {
      return formatMessage(
        { id: 'timesheetLabels.noValueLabel' },
        { label: chipLabel }
      );
    } else if (isSuggestion) {
      return formatMessage(
        { id: 'timesheetLabels.suggestedValue' },
        { label: label?.name, value: chipLabel }
      );
    }
    return formatMessage(
      { id: 'timesheetLabels.selectedValue' },
      { label: label?.name, value: chipLabel }
    );
  }, [chipLabel, formatMessage, isEmpty, isSuggestion, label?.name]);

  const getChipVariant = useCallback(() => {
    if (isEmptyLabelsEnabled) {
      if (labelState === LabelState.SELECTED) {
        return 'filled';
      }

      return 'outlined';
    }
    return !isEmpty && !isSuggestion ? 'filled' : 'outlined';
  }, [labelState, isEmpty, isSuggestion, isEmptyLabelsEnabled]);

  const getChipColor = useCallback(() => {
    if (isEmptyLabelsEnabled) {
      if (
        labelState === LabelState.SELECTED ||
        labelState === LabelState.OMITTED ||
        labelState === LabelState.SUGGESTED
      ) {
        return 'primary';
      }

      return 'error';
    }
    return !isEmpty && !isSuggestion ? 'primary' : 'secondary';
  }, [labelState, isEmpty, isSuggestion, isEmptyLabelsEnabled]);

  const chip = useMemo(
    () => (
      <Chip
        //to skip the tabbing on labels
        tabIndex={-1}
        role={isButton ? 'button' : undefined}
        className={className}
        ref={chipRef}
        aria-label={ariaLabelMessage}
        onMouseOver={showTooltipFn}
        onMouseLeave={hideTooltipFn}
        data-testid={clickableChiptestID}
        label={labelComponent}
        variant={getChipVariant()}
        color={getChipColor()}
        size={size}
        clickable={isChipClickable}
        classes={chipClasses}
      />
    ),
    [
      ariaLabelMessage,
      chipClasses,
      chipRef,
      className,
      clickableChiptestID,
      hideTooltipFn,
      isButton,
      isChipClickable,
      labelComponent,
      showTooltipFn,
      size,
      getChipVariant,
      getChipColor
    ]
  );

  return showTooltip ? (
    //tooltip has unwanted re-render during page load it cause around 160ms for 30 notes with average 9 labels
    <Tooltip title={toolTipText} classes={tooltipClasses}>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

export const ReadonlyLabelChip = memo(ReadonlyLabelChipFN);
